"use client";

import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitives from "@radix-ui/react-dialog";
import { IoLockClosedOutline } from "react-icons/io5";
import { usePathname } from "next/navigation";
import { StaffPinUnlockForm } from "@/components/staff-pin/staff-pin";
import { endAllFormSessions, useInPersonLocked } from "@/hooks/use-in-person-form-lock";

/** Covers this console tab with a PIN panel whenever an in-person form session
 *  is locked (armed when a form is launched, cleared only by a correct PIN or
 *  sign-out). This is how the EMR tab is protected: a patient who leaves the
 *  form and clicks over here hits this gate. It is also the ONLY thing
 *  protecting it, which is why it covers every console route rather than
 *  relying on the form tab staying in front of them.
 *
 *  Built on Radix Dialog primitives so it truly traps focus and marks the rest
 *  of the page inert — Tab can't move from the PIN field into EMR controls
 *  behind it. Escape and outside clicks are swallowed. It is a cover, not a
 *  teardown, so a scribe recording on this tab keeps running underneath. A
 *  correct PIN clears the lock for every tab at once. The tab actually showing
 *  the form (the fill route) is skipped: it has its own Done PIN, and gating it
 *  would hide the form from the patient filling it. */
export const InPersonReturnGate = () => {
  const $ = _c(2);
  const locked = useInPersonLocked();
  const pathname = usePathname();
  const onFillRoute = pathname?.includes("/check-in/forms/fill") ?? false;
  if (!locked || onFillRoute) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DialogPrimitives.Overlay className="fixed inset-0 z-[9998] bg-background" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogPrimitives.Root open={true}><DialogPrimitives.Portal>{t0}<DialogPrimitives.Content className="fixed left-1/2 top-1/2 z-[9999] flex w-[95vw] max-w-sm -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-card p-6 shadow-lg focus:outline-none" onEscapeKeyDown={_temp} onInteractOutside={_temp2}><div className="mb-4 flex flex-col items-center gap-2 text-center"><IoLockClosedOutline className="h-8 w-8 text-foreground-muted" /><DialogPrimitives.Title className="text-xl font-semibold text-foreground">A patient is filling out a form</DialogPrimitives.Title><DialogPrimitives.Description className="text-body text-muted-foreground">Enter your staff PIN to return to Decoda.</DialogPrimitives.Description></div><StaffPinUnlockForm onUnlocked={endAllFormSessions} /></DialogPrimitives.Content></DialogPrimitives.Portal></DialogPrimitives.Root>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(e) {
  return e.preventDefault();
}
function _temp2(e_0) {
  return e_0.preventDefault();
}